<template>
  <v-card dark flat class="practice-card">
    <header class="practice-header">
      <span class="practice-icon"><v-icon>mdi-file-document-edit-outline</v-icon></span>
      <div>
        <div class="eyebrow">Praxisabschluss</div>
        <h2>Praxisaufgabe einreichen</h2>
        <p>{{ instructions }}</p>
      </div>
      <span v-if="latest" class="status-badge" :class="`status-${latest.status}`">
        <v-icon>{{ statusIcon(latest.status) }}</v-icon>{{ statusLabel(latest.status) }}
      </span>
    </header>

    <div v-if="latest" class="submission-summary">
      <div>
        <small>Version</small>
        <strong>{{ latest.attempt }}</strong>
      </div>
      <div>
        <small>Eingereicht</small>
        <strong>{{ formatDate(latest.submittedAt || latest.createdAt) }}</strong>
      </div>
      <v-btn text color="#d4d4d8" :loading="downloading" @click="downloadSubmission(latest)">
        <v-icon left>mdi-download-outline</v-icon>PDF öffnen
      </v-btn>
    </div>

    <div v-if="latest && latest.feedback" class="feedback" :class="{ revision: latest.status === 'revision_required' }">
      <v-icon>{{ latest.status === "revision_required" ? "mdi-comment-alert-outline" : "mdi-comment-check-outline" }}</v-icon>
      <div>
        <strong>Feedback der Academy-Leitung</strong>
        <p>{{ latest.feedback }}</p>
      </div>
    </div>

    <section v-if="canUpload" class="upload-zone" :class="{ selected: selectedFile }">
      <input ref="fileInput" type="file" accept="application/pdf,.pdf" class="native-input" @change="selectFile" />
      <template v-if="!selectedFile">
        <span class="upload-mark"><v-icon>mdi-file-pdf-box</v-icon></span>
        <div>
          <h3>{{ latest ? "Überarbeitete PDF einreichen" : "Deine Arbeit als PDF hochladen" }}</h3>
          <p>Die Datei wird sicher in der Akademie gespeichert. Erlaubt sind PDF-Dateien bis 25 MB.</p>
        </div>
        <v-btn outlined color="#58d388" class="choose-action" @click="$refs.fileInput.click()">
          <v-icon left>mdi-paperclip</v-icon>PDF wählen
        </v-btn>
      </template>
      <template v-else>
        <span class="upload-mark"><v-icon>mdi-file-check-outline</v-icon></span>
        <div class="file-copy">
          <h3>{{ selectedFile.name }}</h3>
          <p>{{ formatSize(selectedFile.size) }} · Cloudflare R2</p>
        </div>
        <v-btn v-if="!busy" icon aria-label="Datei entfernen" @click="clearFile"><v-icon>mdi-close</v-icon></v-btn>
      </template>
    </section>

    <div v-if="busy" class="progress-area">
      <div><span>PDF wird sicher übertragen</span><strong>{{ uploadProgress }}%</strong></div>
      <v-progress-linear :value="uploadProgress" color="#009640" background-color="rgba(255,255,255,0.08)" height="8" rounded />
    </div>
    <v-alert v-if="error" type="error" outlined text class="mt-3 mb-0">{{ error }}</v-alert>

    <footer v-if="canUpload" class="practice-actions">
      <span><v-icon>mdi-shield-check-outline</v-icon>Nur du und die zuständige Academy-Leitung können die Abgabe öffnen.</span>
      <v-btn color="#009640" depressed :disabled="!selectedFile || busy" :loading="busy" @click="submit">
        <v-icon left>mdi-send-outline</v-icon>Zur Prüfung einreichen
      </v-btn>
    </footer>
    <div v-else class="locked-note">
      <v-icon>{{ latest && latest.status === "approved" ? "mdi-check-decagram-outline" : "mdi-clock-check-outline" }}</v-icon>
      <span>
        {{
          latest && latest.status === "approved"
            ? moduleCompleted
              ? "Die Praxisaufgabe ist bestanden und das Modul abgeschlossen."
              : "Die Praxisaufgabe ist bestanden. Schliesse noch die offenen Pflichtlektionen ab, damit das Modul wieder vollständig ist."
            : "Die Abgabe wird geprüft. Du erhältst danach eine Benachrichtigung."
        }}
      </span>
    </div>

    <details v-if="submissions.length > 1" class="history">
      <summary>{{ submissions.length }} eingereichte Versionen anzeigen</summary>
      <div v-for="submission in submissions" :key="submission._id" class="history-row">
        <span>Version {{ submission.attempt }} · {{ formatDate(submission.submittedAt || submission.createdAt) }}</span>
        <strong>{{ statusLabel(submission.status) }}</strong>
        <v-btn icon small aria-label="PDF öffnen" @click="downloadSubmission(submission)"><v-icon small>mdi-download-outline</v-icon></v-btn>
      </div>
    </details>
  </v-card>
</template>

<script>
import api from "@/api"
import { uploadCloudflareMedia } from "@/services/cloudflareMediaUploadService"

const MAX_FILE_SIZE = 25 * 1024 * 1024

export default {
  name: "PracticeSubmission",
  props: {
    moduleId: { type: String, required: true },
    moduleCompleted: { type: Boolean, default: false },
    instructions: {
      type: String,
      default: "Bearbeite die Praxisaufgabe und reiche das Ergebnis als PDF zur persönlichen Prüfung ein.",
    },
  },
  data() {
    return {
      submissions: [],
      selectedFile: null,
      uploadSession: null,
      uploadProgress: 0,
      busy: false,
      downloading: false,
      error: "",
    }
  },
  computed: {
    latest() {
      return this.submissions[0] || null
    },
    canUpload() {
      return !this.latest || this.latest.status === "revision_required"
    },
  },
  mounted() {
    this.loadSubmissions()
  },
  beforeDestroy() {
    if (this.uploadSession) api.akademie.cancelDocumentSubmissionUpload(this.moduleId, this.uploadSession).catch(() => {})
  },
  methods: {
    async loadSubmissions() {
      try {
        const submissions = await api.akademie.fetchMySubmissions({ moduleId: this.moduleId, type: "document_submission" })
        this.submissions = (submissions || []).sort((left, right) => Number(right.attempt || 0) - Number(left.attempt || 0))
      } catch (error) {
        this.error = "Deine bisherigen Abgaben konnten nicht geladen werden."
      }
    },
    selectFile(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ""
      this.error = ""
      if (!file) return
      if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name || "")) {
        this.error = "Bitte wähle eine PDF-Datei aus."
        return
      }
      if (!file.size || file.size > MAX_FILE_SIZE) {
        this.error = "Die PDF-Datei darf maximal 25 MB gross sein."
        return
      }
      this.clearPreparedUpload()
      this.selectedFile = file
    },
    clearFile() {
      this.clearPreparedUpload()
      this.selectedFile = null
      this.uploadProgress = 0
    },
    clearPreparedUpload() {
      const session = this.uploadSession
      this.uploadSession = null
      if (session) api.akademie.cancelDocumentSubmissionUpload(this.moduleId, session).catch(() => {})
    },
    async submit() {
      if (!this.selectedFile || this.busy) return
      this.busy = true
      this.error = ""
      this.uploadProgress = 0
      try {
        this.uploadSession = await api.akademie.prepareDocumentSubmissionUpload(this.moduleId, this.selectedFile)
        await uploadCloudflareMedia(this.selectedFile, this.uploadSession, {
          onProgress: (loaded, total) => {
            this.uploadProgress = Math.round((loaded * 100) / Math.max(1, total || this.selectedFile.size))
          },
        })
        await api.akademie.completeDocumentSubmissionUpload(this.moduleId, {
          objectKey: this.uploadSession.objectKey,
          sessionToken: this.uploadSession.sessionToken,
        })
        this.uploadSession = null
        this.selectedFile = null
        this.uploadProgress = 100
        await this.loadSubmissions()
        this.$emit("submitted")
      } catch (error) {
        this.error =
          (error.response && error.response.data && (error.response.data.error || error.response.data.message)) ||
          "Die Praxisaufgabe konnte nicht eingereicht werden."
      } finally {
        this.busy = false
      }
    },
    async downloadSubmission(submission) {
      if (!submission || this.downloading) return
      this.downloading = true
      try {
        await api.akademie.downloadFile(
          `${api.akademie.getSubmissionFilePath(submission._id)}?download=1`,
          (submission.mediaFile && submission.mediaFile.originalName) || `Praxisaufgabe-Version-${submission.attempt}.pdf`,
        )
      } catch (error) {
        this.error = "Die PDF-Datei konnte nicht geöffnet werden."
      } finally {
        this.downloading = false
      }
    },
    statusLabel(status) {
      return { submitted: "Eingereicht", in_review: "In Prüfung", approved: "Bestanden", revision_required: "Überarbeitung" }[status] || "Eingereicht"
    },
    statusIcon(status) {
      if (status === "approved") return "mdi-check-circle-outline"
      if (status === "revision_required") return "mdi-reload"
      return "mdi-clock-outline"
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString("de-CH", { dateStyle: "medium", timeStyle: "short" }) : "-"
    },
    formatSize(bytes) {
      return `${(Number(bytes || 0) / 1024 / 1024).toFixed(1)} MB`
    },
  },
}
</script>

<style scoped>
.practice-card {
  margin-top: 1rem;
  padding: 1.25rem;
  border: 1px solid rgba(88, 211, 136, 0.22);
  border-radius: 1.25rem !important;
  background: radial-gradient(circle at 100% 0, rgba(0, 150, 64, 0.11), transparent 22rem), rgba(255, 255, 255, 0.035) !important;
  color: #f4f4f5;
  font-size: 1rem;
}

.practice-header {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: start;
  gap: 0.875rem;
}

.practice-icon,
.upload-mark {
  display: grid;
  place-items: center;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.14);
}

.practice-icon {
  width: 3rem;
  height: 3rem;
  border-radius: 0.875rem;
}

.practice-icon .v-icon,
.upload-mark .v-icon {
  color: currentColor;
}

.eyebrow {
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.practice-header h2,
.upload-zone h3 {
  margin: 0.1875rem 0 0.25rem;
  color: #fff;
  font-size: 1.25rem;
  line-height: 1.3;
}

.practice-header p,
.upload-zone p,
.feedback p {
  margin: 0;
  color: #b8b8bc;
  font-size: 0.875rem;
  line-height: 1.55;
}

.status-badge {
  display: inline-flex;
  min-height: 2rem;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.625rem;
  border-radius: 999px;
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.13);
  font-size: 0.75rem;
  font-weight: 750;
}

.status-badge .v-icon {
  color: currentColor;
  font-size: 1rem;
}

.status-approved {
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
}

.status-revision_required {
  color: #ff9aa5;
  background: rgba(244, 63, 94, 0.12);
}

.submission-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  padding: 0.875rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.875rem;
  background: rgba(0, 0, 0, 0.14);
}

.submission-summary > div {
  display: grid;
  gap: 0.125rem;
}

.submission-summary small {
  color: #8f9095;
  font-size: 0.75rem;
}

.submission-summary strong {
  font-size: 0.875rem;
}

.submission-summary .v-btn {
  margin-left: auto;
  font-size: 0.875rem;
  text-transform: none;
}

.feedback {
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 0.875rem;
  border-left: 0.25rem solid #58d388;
  border-radius: 0.75rem;
  background: rgba(0, 150, 64, 0.08);
}

.feedback.revision {
  border-left-color: #ff7b86;
  background: rgba(244, 63, 94, 0.08);
}

.feedback .v-icon {
  color: #7ee5a6;
}

.feedback.revision .v-icon {
  color: #ff9aa5;
}

.feedback strong {
  color: #fff;
  font-size: 0.875rem;
}

.upload-zone {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.875rem;
  margin-top: 1rem;
  padding: 1rem;
  border: 1px dashed rgba(88, 211, 136, 0.35);
  border-radius: 1rem;
  background: rgba(0, 150, 64, 0.04);
}

.upload-mark {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.8125rem;
}

.upload-zone h3 {
  font-size: 1rem;
}

.native-input {
  display: none;
}

.choose-action,
.practice-actions .v-btn {
  min-height: 2.75rem;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem;
  font-weight: 750;
  text-transform: none;
}

.progress-area {
  margin-top: 1rem;
}

.progress-area > div {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  color: #b8b8bc;
  font-size: 0.875rem;
}

.practice-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1rem;
}

.practice-actions > span,
.locked-note {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
}

.practice-actions > span .v-icon,
.locked-note .v-icon {
  color: #7ee5a6;
}

.locked-note {
  margin-top: 1rem;
  padding: 0.875rem;
  border-radius: 0.875rem;
  background: rgba(0, 150, 64, 0.08);
}

.history {
  margin-top: 1rem;
  color: #b8b8bc;
  font-size: 0.875rem;
}

.history summary {
  min-height: 2.5rem;
  padding: 0.625rem 0;
  cursor: pointer;
  color: #d4d4d8;
  font-weight: 700;
}

.history-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.history-row strong {
  color: #d4d4d8;
  font-size: 0.75rem;
}

@media (max-width: 43rem) {
  .practice-header,
  .upload-zone {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .status-badge,
  .choose-action,
  .upload-zone > .v-btn:last-child {
    grid-column: 2;
    justify-self: start;
  }

  .practice-actions {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
